<template>
  <div>
    <loading-provider :loading="loading">
      <v-card
        class="pa-4"
        elevation="0"
      >
        <page-sub-header class="mb-6">
          <div>详细介绍</div>
          <template #subtitle>
            在这里设置会员系统的详细介绍图片和描述文本
          </template>
          <template #action>
            <v-btn
              :disabled="!formValid"
              color="amber lighten-4 black--text"
              elevation="0"
              @click="saveChanges"
            >
              <v-icon left>
                mdi-content-save
              </v-icon>
              保存
            </v-btn>
          </template>
        </page-sub-header>

        <v-form
          ref="form"
          v-model="formValid"
          lazy-validation
        >
          <v-tabs
            v-model="activeTab"
            color="primary"
            grow
          >
            <v-tab>系统介绍图片</v-tab>
            <v-tab>详细描述文本</v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <!-- Tab 1: Membership System Introduction Image -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <v-col cols="12">
                      <div class="mb-2">
                        <div class="font-weight-bold">
                          系统介绍图片<span v-if="required">*</span>
                        </div>
                        <div class="text-body-2">
                          上传会员系统介绍长图片 (JPG, PNG)，推荐尺寸：750x1200像素，5:8比例
                        </div>
                      </div>

                      <!-- Enhanced Image Uploader with special styling for long images -->
                      <div class="long-image-uploader">
                        <enhanced-image-uploader
                          v-model="localSystemInfo.promotionalImageUrl"
                          :max-size-m-b="2"
                          hint="上传会员系统介绍长图片 (JPG, PNG)，推荐尺寸：750x1200像素，5:8比例"
                        />
                      </div>

                      <!-- Image Preview Section (if image exists) -->
                      <div
                        v-if="localSystemInfo.promotionalImageUrl"
                        class="mt-4"
                      >
                        <div class="font-weight-bold mb-2">
                          图片预览
                        </div>
                        <div class="long-image-preview-container">
                          <img
                            :src="localSystemInfo.promotionalImageUrl"
                            class="long-image-preview"
                            alt="Membership System Introduction Image"
                          >
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Tab 2: Detailed Description Text -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <v-col cols="12">
                      <div class="mb-2">
                        <div class="font-weight-bold">
                          详细描述文本<span v-if="required">*</span>
                        </div>
                        <div class="text-body-2">
                          会员系统的详细介绍文本
                        </div>
                      </div>

                      <!-- Markdown Editor for Description Text -->
                      <MarkdownEditor
                        v-model="localSystemInfo.descriptionTextMD"
                        height="400px"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-form>
      </v-card>
    </loading-provider>
  </div>
</template>

<script>
import LoadingProvider from '@/views/widget/LoadingProvider.vue'
import PageSubHeader from '@/views/widget/typography/PageSubHeader.vue'
import EnhancedImageUploader from '@/model/baseForm/fields/EnhancedImageUploader.vue'
import { saveFile, showSuccessMessage } from '@/model/utils/utils'

export default {
  name: 'MembershipSystemIntroduction',
  components: {
    LoadingProvider,
    PageSubHeader,
    EnhancedImageUploader
  },
  props: {
    systemInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: false,
      formValid: true,
      required: false,
      activeTab: 0,
      localSystemInfo: {
        promotionalImageUrl: '',
        descriptionTextMD: ''
      }
    }
  },
  watch: {
    systemInfo: {
      immediate: true,
      deep: true,
      handler (val) {
        if (val && Object.keys(val).length > 0) {
          // Deep copy to ensure we don't modify the original
          const deepCopy = JSON.parse(JSON.stringify(val))

          // Update local properties
          this.localSystemInfo.promotionalImageUrl = deepCopy.promotionalImageUrl || ''
          this.localSystemInfo.descriptionTextMD = deepCopy.descriptionTextMD || ''
        }
      }
    }
  },
  methods: {
    async saveChanges () {
      if (!this.$refs.form.validate()) return

      this.loading = true
      try {
        // Process image upload if it's a File object
        if (this.localSystemInfo.promotionalImageUrl &&
            typeof this.localSystemInfo.promotionalImageUrl !== 'string') {
          this.localSystemInfo.promotionalImageUrl = await saveFile(this.localSystemInfo.promotionalImageUrl)
        }

        // Create updated system info object
        const updatedInfo = {
          ...this.systemInfo,
          promotionalImageUrl: this.localSystemInfo.promotionalImageUrl,
          descriptionTextMD: this.localSystemInfo.descriptionTextMD
        }

        // Emit update event
        this.$emit('update:system-info', updatedInfo)
        showSuccessMessage('保存成功！')
      } catch (error) {
        console.error('Error saving membership system introduction:', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.long-image-uploader {
  width: 100%;
}

.long-image-preview-container {
  width: 100%;
  max-height: 600px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #f5f5f5;
}

.long-image-preview {
  width: 100%;
  height: auto;
  display: block;
}

/* Custom scrollbar for the image preview */
.long-image-preview-container::-webkit-scrollbar {
  width: 8px;
}

.long-image-preview-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.long-image-preview-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.long-image-preview-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
